import React, { useEffect, useRef, useState } from 'react';

const LabelDesigner = ({ onDesignChange }) => {
  const fpdContainerRef = useRef(null);
  const fpdInstanceRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadFPD = async () => {
      try {
        // Check if FancyProductDesigner is already loaded AND custom elements are registered
        if (window.FancyProductDesigner && window.customElements.get('fpd-dropdown')) {
          console.log('✅ FPD already loaded, reusing existing instance');
          initializeFPD();
          return;
        }

        // Check if FPD is loaded but custom elements failed - reload page
        if (window.FancyProductDesigner && !window.customElements.get('fpd-dropdown')) {
          console.warn('⚠️ FPD loaded but custom elements missing, reloading...');
          window.location.reload();
          return;
        }

        // Check if CSS is already loaded
        if (!document.querySelector('link[href="/fpd/css/FancyProductDesigner.min.css"]')) {
          const cssLink = document.createElement('link');
          cssLink.rel = 'stylesheet';
          cssLink.type = 'text/css';
          cssLink.href = '/fpd/css/FancyProductDesigner.min.css';
          document.head.appendChild(cssLink);
        }

        // Check if Fabric.js is already loaded
        if (!window.fabric) {
          const fabricScript = document.createElement('script');
          fabricScript.src = '/fpd/js/fabric.min.js';
          
          fabricScript.onload = () => {
            loadFPDScript();
          };
          
          fabricScript.onerror = () => {
            console.error('❌ Failed to load Fabric.js');
            setError('Errore nel caricamento delle dipendenze');
          };
          
          document.body.appendChild(fabricScript);
        } else {
          loadFPDScript();
        }

        function loadFPDScript() {
          // Check if FPD script is already loaded
          if (document.querySelector('script[src="/fpd/js/FancyProductDesigner.min.js"]')) {
            console.log('✅ FPD Script already loaded');
            // Add delay to ensure custom elements are ready
            setTimeout(initializeFPD, 500);
            return;
          }

          const fpdScript = document.createElement('script');
          fpdScript.src = '/fpd/js/FancyProductDesigner.min.js';
          
          fpdScript.onload = () => {
            console.log('✅ FPD Scripts loaded successfully');
            // Add delay to ensure custom elements are registered
            setTimeout(initializeFPD, 500);
          };
          
          fpdScript.onerror = () => {
            console.error('❌ Failed to load FPD script');
            setError('Errore nel caricamento del designer');
          };
          
          document.body.appendChild(fpdScript);
        }

      } catch (error) {
        console.error('❌ FPD Loading Error:', error);
        setError('Errore durante l\'inizializzazione');
      }
    };

    const initializeFPD = () => {
      if (!fpdContainerRef.current || fpdInstanceRef.current) {
        console.log('⏭️ FPD already initialized or container not ready');
        return;
      }

      // Check if custom elements are properly registered
      if (!window.customElements.get('fpd-dropdown')) {
        console.warn('⚠️ FPD custom elements not ready, retrying in 1 second...');
        setTimeout(initializeFPD, 1000);
        return;
      }

      try {
        console.log('🚀 Initializing FPD with custom elements ready...');

        // FPD Configuration
        const fpdOptions = {
          // Basic settings
          editorMode: false,
          templatesPanel: false,
          imageUploads: true,
          
          // Layout settings
          sidebarPanel: {
            element: false // We'll handle our own UI
          },
          
          // Initial product configuration for label design
          initialElements: [
            {
              type: 'text',
              text: 'Il Tuo Amaro',
              left: 100,
              top: 50,
              fontSize: 24,
              fontFamily: 'Arial',
              fill: '#8B4513'
            }
          ],
          
          // Canvas settings
          canvasWidth: 400,
          canvasHeight: 300,
          
          // Color picker settings
          colorPicker: {
            colors: ['#8B4513', '#D2691E', '#CD853F', '#F4A460', '#DEB887', '#BC8F8F']
          }
        };

        // Initialize FPD
        const fpd = new window.FancyProductDesigner(fpdContainerRef.current, fpdOptions);
        fpdInstanceRef.current = fpd;

        // Event listeners
        fpd.addEventListener('ready', () => {
          console.log('✅ FPD Ready!');
          setIsReady(true);
          
          // Add initial product/view for label design
          fpd.addProduct([{
            title: 'Etichetta Liquore',
            thumbnail: '',
            options: {
              stage: {
                width: 400,
                height: 300
              }
            }
          }]);
        });

        fpd.addEventListener('elementAdd', (evt) => {
          console.log('Element added:', evt.detail);
          handleDesignChange();
        });

        fpd.addEventListener('elementModify', (evt) => {
          console.log('Element modified:', evt.detail);
          handleDesignChange();
        });

        fpd.addEventListener('elementRemove', (evt) => {
          console.log('Element removed:', evt.detail);
          handleDesignChange();
        });

      } catch (error) {
        console.error('❌ FPD Initialization Error:', error);
        setError('Errore durante l\'inizializzazione del designer');
      }
    };

    const handleDesignChange = () => {
      if (!fpdInstanceRef.current || !isReady) return;
      
      try {
        // Export current design as data URL
        const currentView = fpdInstanceRef.current.currentViewInstance;
        if (currentView) {
          const dataURL = currentView.toDataURL({
            format: 'png',
            quality: 0.8,
            multiplier: 2
          });
          
          // Get design configuration for saving
          const productData = fpdInstanceRef.current.getProduct();
          
          if (onDesignChange) {
            onDesignChange({
              imageDataURL: dataURL,
              designData: productData,
              timestamp: new Date().toISOString()
            });
          }
        }
      } catch (error) {
        console.error('❌ Design export error:', error);
      }
    };

    loadFPD();

    // Cleanup
    return () => {
      if (fpdInstanceRef.current) {
        // Cleanup if FPD provides destroy method
        try {
          fpdInstanceRef.current = null;
        } catch (error) {
          console.warn('FPD cleanup warning:', error);
        }
      }
    };
  }, []);

  if (error) {
    return (
      <div className="flex items-center justify-center h-64 bg-red-50 border border-red-200 rounded-lg">
        <div className="text-center">
          <div className="text-red-600 text-lg font-medium mb-2">Errore Designer</div>
          <div className="text-red-500 text-sm">{error}</div>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Ricarica Pagina
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="label-designer-container">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Designer Etichetta Personalizzata
        </h3>
        <p className="text-sm text-gray-600">
          Personalizza la tua etichetta con testo, colori e elementi grafici
        </p>
      </div>
      
      <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
        <div 
          ref={fpdContainerRef}
          className="fpd-container"
          style={{ minHeight: '400px' }}
        />
        
        {!isReady && (
          <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-2"></div>
              <div className="text-gray-600">Caricamento designer...</div>
            </div>
          </div>
        )}
      </div>

      {isReady && (
        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center">
            <div className="text-green-600 font-medium">✅ Designer Pronto!</div>
            <div className="ml-auto text-sm text-green-600">
              Usa i controlli per personalizzare la tua etichetta
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LabelDesigner;